<template>
    <div>
        <div class="search-container">
            <div class="search-component">
                <search-component
                    placeholder="Search all fields"
                    v-if="$root.isDesktop"
                    :search-filter="currentSearchTextValue"
                    :handle-search-on-type="true"
                    @onSearch="handleFilterOrSearch"
                />
            </div>

            <div class="action-container">
                <md-button class="md-primary" @click="navigateToCreateOrder">
                    <md-icon>add</md-icon>
                    Create Order
                </md-button>
            </div>

            <div class="filter-container">
                <grid-filter-manager
                    v-if="gridApi"
                    :grid-api="gridApi"
                    :get-search-text="() => currentSearchTextValue"
                    :set-search-text="
                        (v) => {
                            currentSearchTextValue = v;
                            refreshServerSide();
                        }
                    "
                    grid-url="/api/orders/grid/saved"
                    @default-filter-loaded="filterHasLoaded"
                />
            </div>
        </div>

        <div>
            <bulk-order-actions
                :selected-orders="selectedRows"
                :actions-to-display="tableHeaderActionsToDisplay"
                @carrier-assign="handleCarrierAssign"
            />
        </div>

        <div class="grid-wrapper">
            <ag-grid-vue
                :style="{ width: '100%', height: isCarrierMarketplaceEnabled ? '75vh' : '85vh' }"
                class="ag-theme-quartz"
                :column-defs="columnDefs"
                :row-data="rowData"
                :default-col-def="defaultColDef"
                :grid-options="gridOptions"
                @grid-ready="onGridReady"
                :context="context"
                @selection-changed="onSelectionChanged"
            ></ag-grid-vue>
        </div>
    </div>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridVue } from 'ag-grid-vue';
import { handleRequests, showErrorMessage } from '@/helpers';
import LinkRenderer from '@/AgGridComponents/link-renderer';
import StatusBadgeRenderer from '@/AgGridComponents/status-badge-renderer';
import ActionRenderer from '@/pages/Orders/grid-components/action-renderer';
import { ORDER_FULFILLMENT_STATUS_CONSTANTS, FEATURE_NAMES } from '@/utils/constants';
import BulkOrderActions from '@/pages/Orders/grid-components/bulk-actions';
import { SearchComponent } from '@/components';
import { mapGetters } from 'vuex';
import GridFilterManager from '@/AgGridComponents/grid-filter-manager';
import PageHeaderCheckbox from '@/AgGridComponents/page-header-checkbox';

import { FeatureManager } from '@/directives';
import { OrderMixin, AgGridMixin } from '@/mixins';

export default {
    name: 'OrderGridOverview',
    mixins: [FeatureManager, OrderMixin, AgGridMixin],
    components: {
        AgGridVue,
        SearchComponent,
        StatusBadgeRenderer,
        ActionRenderer,
        LinkRenderer,
        BulkOrderActions,
        GridFilterManager,
        PageHeaderCheckbox
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        }),
        isCarrierMarketplaceEnabled() {
            return this.$_hasFeature(FEATURE_NAMES.CarrierMarketplace);
        }
    },
    data() {
        return {
            context: null,
            tableHeaderActionsToDisplay: ['sendSchedulingLink'],
            currentSearchTextValue: '',
            selectedRows: [],
            gridApi: null,
            rowData: [],
            customerList: [], // Add customerList to data,
            serviceAreaList: [],
            servicePackageList: [],
            teamRegionList: [],
            handlingMethodList: [],
            serviceAreaLoading: true,
            customerListLoading: true,
            servicePackageLoading: true,
            teamRegionListLoading: true,
            handlingMethodListLoading: true,
            defaultColDef: {
                resizable: true,
                sortable: true,
                floatingFilter: false, // Set to false by default, enabled per column as needed
                suppressSizeToFit: false, // Allow auto-sizing
                filterParams: {
                    debounceMs: 1000,
                    maxNumConditions: 1
                }
            },
            orderKeys: ['itemCount', 'volume', 'weight'],
            gridOptions: {
                // eslint-disable-next-line consistent-return
                getRowStyle: (params) => {
                    if (params.node.level !== 0) {
                        return { 'font-weight': 600 };
                    }
                },
                onPaginationChanged: (params) => {
                    this.updateCacheBlockDefaultSize(params.api);
                    this.$_updatePageTotals(params.api, this.orderKeys);
                },
                rowSelection: 'multiple',
                rowModelType: 'serverSide', // Enable server-side row model
                serverSideStoreType: 'partial', // Use partial store for better performance
                cacheBlockSize: 50,
                maxBlocksInCache: 5, // Maximum number of blocks to cache
                suppressRowClickSelection: true,
                headerHeight: 40, // Increase header height for dropdown
                floatingFiltersHeight: 40, // Height for floating filter row
                animateRows: true, // Smooth row animations

                pagination: true,
                paginationPageSize: 50, // Default page size
                paginationPageSizeSelector: [50, 100, 200, 500], // Page size options
                onFirstDataRendered: (params) => {
                    // Auto-size all columns based on content after first data load
                    params.api.autoSizeAllColumns();
                },
                // Sidebar configuration
                sideBar: {
                    toolPanels: [
                        {
                            id: 'columns',
                            labelDefault: 'Columns',
                            labelKey: 'columns',
                            iconKey: 'columns',
                            toolPanel: 'agColumnsToolPanel',
                            toolPanelParams: {
                                suppressRowGroups: true,
                                suppressValues: true,
                                suppressPivots: true,
                                suppressPivotMode: true
                            }
                        }
                    ],
                    defaultToolPanel: '', // Empty string means collapsed on initial load
                    hiddenByDefault: false
                }
            },
            columnDefs: [],
            weightSymbol: '',
            volumeSymbol: '',
            hasGridfilterLoaded: false,
            pinnedBottomRowData: [],
            lastCacheBlockSize: null
        };
    },
    beforeMount() {
        this.lastCacheBlockSize = this.gridOptions.cacheBlockSize;
        this.columnDefs = this.buildColumnDefs();
        this.context = {
            componentParent: this
        };

        this.gridOptions.getRowId = (params) => params.data.orderId;
    },
    mounted() {
        if (this.isCarrierMarketplaceEnabled) {
            this.tableHeaderActionsToDisplay.push('batchAssign');
        }

        if (this.user.vehicleCapacityUnitsConfiguration != null) {
            this.weightSymbol = this.user.vehicleCapacityUnitsConfiguration.find((x) => x.type === 'weight').symbol;
            this.volumeSymbol = this.user.vehicleCapacityUnitsConfiguration.find((x) => x.type === 'volume').symbol;
        }
    },
    methods: {
        updateCacheBlockDefaultSize(api) {
            const currentPageSize = api.paginationGetPageSize();
            if (this.lastCacheBlockSize !== currentPageSize) {
                this.lastCacheBlockSize = currentPageSize;
                api.setCacheBlockSize(currentPageSize);
            }
        },
        buildColumnDefs() {
            return [
                {
                    headerName: '',
                    checkboxSelection: true,
                    headerComponent: 'PageHeaderCheckbox',
                    minWidth: 50,
                    maxWidth: 50,
                    lockPosition: 'left',
                    sortable: false,
                    filter: false,
                    suppressMenu: true,
                    resizable: false
                },
                {
                    headerName: 'Order Number',
                    field: 'orderNumber',
                    cellRenderer: 'LinkRenderer',
                    cellRendererParams: (params) => {
                        return this.$_linkCellRenderer('Order Details', { orderId: params.data.orderId }, params.value);
                    },
                    filter: 'agTextColumnFilter',
                    floatingFilter: true,
                    pinned: 'left'
                },
                {
                    headerName: 'Source Reference',
                    field: 'sourceReference',
                    filter: 'agTextColumnFilter',
                    cellRenderer: 'LinkRenderer',
                    cellRendererParams: (params) => {
                        return this.$_linkCellRenderer('Order Details', { orderId: params.data.orderId }, params.value);
                    },
                    floatingFilter: true,
                    pinned: 'left'
                },
                {
                    headerName: 'Status',
                    field: 'status',
                    cellRenderer: 'StatusBadgeRenderer',
                    cellRendererParams: (params) => {
                        return {
                            status: params.value,
                            badgeColor: `status-${this.$_getBadgeStatusColor(params.value)}`
                        };
                    },
                    filter: 'agSetColumnFilter',
                    floatingFilter: true,
                    filterParams: {
                        values: ORDER_FULFILLMENT_STATUS_CONSTANTS.map((status) => status.name),
                        suppressSelectAll: false,
                        suppressMiniFilter: false,
                        selectAllOnMiniFilter: true
                    }
                },
                {
                    headerName: 'Created Date',
                    field: 'createdDate',
                    filter: 'agDateColumnFilter',
                    floatingFilter: true,
                    cellRenderer: (params) => {
                        return this.$_formatDateFromUTC(params.value);
                    }
                },
                {
                    headerName: 'Order Date',
                    field: 'orderDate',
                    filter: 'agDateColumnFilter',
                    floatingFilter: true,
                    cellRenderer: (params) => {
                        return this.$_formatDate(params.value);
                    }
                },
                {
                    headerName: 'Ready Delivery Date',
                    field: 'readyDeliveryAvailableDate',
                    filter: 'agDateColumnFilter',
                    floatingFilter: true,
                    cellRenderer: (params) => {
                        return this.$_formatDate(params.value);
                    }
                },
                {
                    headerName: 'Expected Delivery Date',
                    field: 'expectedDeliveryDate',
                    filter: 'agDateColumnFilter',
                    floatingFilter: true,
                    cellRenderer: (params) => {
                        return this.$_formatDate(params.value);
                    }
                },
                {
                    field: 'shippingAddress',
                    filter: 'agTextColumnFilter',
                    filterParams: {
                        filterOptions: ['contains', 'equals', 'startsWith'],
                        caseSensitive: false
                    },
                    floatingFilter: true
                },
                {
                    field: 'stopType',
                    filter: 'agSetColumnFilter',
                    floatingFilter: true,
                    filterParams: {
                        values: ['Drop', 'Pick Up', 'Various', 'Blank'],
                        suppressSelectAll: false,
                        suppressMiniFilter: false,
                        selectAllOnMiniFilter: true,
                        maxNumConditions: 1
                    },
                    sortable: false
                },
                {
                    field: 'customerName',
                    filter: 'agSetColumnFilter',
                    floatingFilter: true,
                    keyCreator: (params) => {
                        return this.$_formatKeyCreator(params.value, params.value.customerId);
                    },
                    filterParams: {
                        values: this.loadCustomerList, // Start with empty array, will be populated when data loads
                        valueFormatter: (params) => {
                            return params.value ? params.value.name : '';
                        },
                        suppressSelectAll: false,
                        suppressMiniFilter: false,
                        selectAllOnMiniFilter: true
                    }
                },
                {
                    field: 'serviceArea',
                    filter: 'agSetColumnFilter',
                    floatingFilter: true,
                    filterParams: {
                        values: this.loadServiceAreaList, // Start with empty array, will be populated when data loads
                        valueFormatter: (params) => {
                            return params.value ? params.value.name : '';
                        },
                        keyCreator: (params) => {
                            return this.$_formatKeyCreator(params.value, params.value.serviceAreaId);
                        },
                        suppressSelectAll: false,
                        suppressMiniFilter: false,
                        selectAllOnMiniFilter: true
                    }
                },
                {
                    field: 'servicePackage',
                    filter: 'agSetColumnFilter',
                    floatingFilter: true,
                    keyCreator: (params) => {
                        return this.$_formatKeyCreator(params.value, params.value.rateGroupId);
                    },
                    filterParams: {
                        values: this.loadServicePackageList, // Start with empty array, will be populated when data loads
                        valueFormatter: (params) => {
                            return params.value ? params.value.rateGroupName : '';
                        },
                        suppressSelectAll: false,
                        suppressMiniFilter: false,
                        selectAllOnMiniFilter: true
                    }
                },
                {
                    headerName: 'Team Region',
                    field: 'teamRegion',
                    filter: 'agSetColumnFilter',
                    floatingFilter: true,
                    valueGetter: (params) => {
                        return params.data.teamRegion ? params.data.teamRegion.name : '';
                    },
                    keyCreator: (params) => {
                        return this.$_formatKeyCreator(params.value, params.data.teamRegion?.teamRegionId);
                    },
                    filterParams: {
                        values: this.loadTeamRegionList,
                        valueFormatter: (params) => {
                            return params.value ? params.value.name : '';
                        },
                        keyCreator: (params) => {
                            return this.$_formatKeyCreator(params.value, params.value?.teamRegionId);
                        },
                        suppressSelectAll: false,
                        suppressMiniFilter: false,
                        selectAllOnMiniFilter: true
                    }
                },
                {
                    field: 'shippingContact',
                    filter: 'agTextColumnFilter',
                    filterParams: {
                        filterOptions: ['contains', 'equals', 'startsWith'],
                        caseSensitive: false
                    },
                    floatingFilter: true
                },
                {
                    field: 'handlingMethod',
                    filter: 'agSetColumnFilter',
                    floatingFilter: true,
                    filterParams: {
                        values: this.loadHandlingMethod // Start with empty array, will be populated when data loads
                    }
                },
                {
                    field: 'itemCount',
                    filter: 'agNumberColumnFilter',
                    aggFunc: 'sum',
                    filterParams: {
                        filterOptions: [
                            'equals',
                            'greaterThan',
                            'lessThan',
                            'inRange',
                            'lessThanOrEqual',
                            'greaterThanOrEqual'
                        ]
                    },
                    floatingFilter: true
                },
                {
                    field: 'volume',
                    filter: 'agNumberColumnFilter',
                    floatingFilter: true,
                    aggFunc: 'sum',
                    valueFormatter: (params) => {
                        if (params.value == null) 
                            return null;

                        return `${params.value.toFixed(2)} ${this.volumeSymbol}`;
                    }
                },
                {
                    field: 'weight',
                    filter: 'agNumberColumnFilter',
                    floatingFilter: true,
                    aggFunc: 'sum',
                    valueFormatter: (params) => {
                        if (params.value == null) 
                            return null;

                        return `${params.value.toFixed(2)} ${this.weightSymbol}`;
                    }
                },
                {
                    field: 'notes'
                },
                {
                    field: 'deliveryInstructions'
                },
                {
                    headerName: 'Latest Order Note',
                    field: 'latestOrderNote',
                    hide: true
                },
                {
                    headerName: 'Actions',
                    cellRenderer: 'ActionRenderer',
                    suppressMenu: true,
                    sortable: false,
                    filter: false
                },
                {
                    headerName: 'Earliest Contact Date',
                    field: 'earliestContactDate',
                    filter: 'agDateColumnFilter',
                    floatingFilter: true,
                    cellRenderer: (params) => {
                        return this.$_formatDateFromUTC(params.value);
                    }
                },
                {
                    headerName: 'Most Recent Contact Date',
                    field: 'latestContactDate',
                    filter: 'agDateColumnFilter',
                    floatingFilter: true,
                    cellRenderer: (params) => {
                        return this.$_formatDateFromUTC(params.value);
                    }
                }
            ];
        },
        async onGridReady(params) {
            this.gridApi = params.api;

            if (this.hasGridfilterLoaded) {
                this.setupDataList();
            }
        },

        setupDataList() {
            const datasource = this.$_createDataSource(
                '/api/orders',
                this.gridApi,
                () => this.currentSearchTextValue, // getter function to get current search text
                this.transformData
            );

            this.gridApi.setServerSideDatasource(datasource);
        },

        transformData(responses) {
            return responses.map((order) => ({
                ...order,
                shippingContact: order.contact != null ? order.contact.name : '',
                shippingAddress: this.getShippingAddress(order.shippingAddress),
                customerName: order.customer != null ? order.customer.name : '',
                servicePackage: order.rateGroup != null ? order.rateGroup.rateGroupName : '',
                teamRegion: order.teamRegionName
                    ? { teamRegionId: order.teamRegionId, name: order.teamRegionName }
                    : null
            }));
        },
        getShippingAddress(shippingAddress) {
            if (!shippingAddress) {
                return '';
            }

            const { city, provinceCode } = shippingAddress;

            if (city && provinceCode) {
                return `${city}, ${provinceCode}`;
            }

            return city || '';
        },

        onSelectionChanged() {
            // getSelectedRows is not working and returning 0, so we need to manually track selected rows
            const selectedRows = [];
            this.gridApi.forEachNode((node) => {
                if (node.isSelected()) {
                    selectedRows.push(node.data);
                }
            });
            this.selectedRows = selectedRows;
            this.$emit('selection-changed', selectedRows);
        },

        handleCarrierAssign(event) {
            if (event) {
                const rowsToRemove = [];
                const selectedOrderIds = this.selectedRows.map((row) => row.orderId.toString());

                this.gridApi.forEachNode((node) => {
                    if (selectedOrderIds.includes(node.data.orderId.toString())) {
                        rowsToRemove.push({
                            orderId: node.data.orderId.toString()
                        });
                    }
                });

                this.gridApi.applyServerSideTransaction({ remove: rowsToRemove });
                this.gridApi.deselectAll();
                this.selectedRows = [];
            }
        },

        getSelectedRowData() {
            const selectedData = this.gridApi.getSelectedRows();
            return selectedData;
        },

        async loadHandlingMethod(params) {
            try {
                const endpoint = '/api/orders/handling-method';
                const response = await handleRequests(endpoint);

                if (response.data && response.data.length > 0) {
                    this.handlingMethodList = response.data;
                    this.handlingMethodList.push(null);
                } else {
                    this.handlingMethodList = [];
                }

                params.success(this.handlingMethodList);
            } catch (error) {
                console.error('Error loading customer list:', error);
                const message = 'Error in getting the customer list';
                showErrorMessage(this, message, error);
                this.handlingMethodList = [];
                params.success(this.handlingMethodList);
            } finally {
                this.handlingMethodListLoading = false;
            }
        },

        async loadCustomerList(params) {
            try {
                const endpoint = '/api/customers/allCustomers';
                const response = await handleRequests(endpoint);

                if (response.data.customers && response.data.customers.length > 0) {
                    this.customerList = response.data.customers.map((cl) => ({
                        customerId: cl.customerId,
                        name: cl.name
                    }));
                    this.customerList.push({ name: '(Blank)', customerId: null });
                } else {
                    this.customerList = [];
                }
                params.success(this.customerList);
            } catch (error) {
                console.error('Error loading customer list:', error);
                const message = 'Error in getting the customer list';
                showErrorMessage(this, message, error);
                this.customerList = [];
                params.success(this.customerList);
            } finally {
                this.customerListLoading = false;
            }
        },

        async loadServicePackageList(params) {
            try {
                const endpoint = `/api/rate-groups/rateRules`;
                const response = await handleRequests(endpoint);

                if (response.data && response.data.length > 0) {
                    this.servicePackageList = response.data.map((rg) => {
                        return {
                            rateGroupId: rg.rateGroupId,
                            rateGroupName: rg.rateGroupName
                        };
                    });
                    this.servicePackageList.push({ rateGroupId: null, rateGroupName: '(Blank)' });
                } else {
                    this.servicePackageList = [];
                }

                params.success(this.servicePackageList);
            } catch (error) {
                const message = 'Error in getting the rate groups list';
                showErrorMessage(this, message, error);
                this.servicePackageList = [];
                params.success(this.servicePackageList);
            } finally {
                this.servicePackageLoading = false;
            }
        },

        async loadServiceAreaList(params) {
            try {
                const endpoint = '/api/teams/service-areas?includeSuburbLocalities=false';
                const response = await handleRequests(endpoint);

                if (response.data && response.data.length > 0) {
                    this.serviceAreaList = response.data.map((x) => {
                        return {
                            serviceAreaId: x.serviceAreaId,
                            name: x.name
                        };
                    });
                    this.serviceAreaList.push({ serviceAreaId: null, name: '(Blank)' });
                } else {
                    this.serviceAreaList = [];
                }
                params.success(this.serviceAreaList);
            } catch (error) {
                const message = 'Error in getting the service area list';
                showErrorMessage(this, message, error);
                this.serviceAreaList = [];
                params.success(this.serviceAreaList);
            } finally {
                this.serviceAreaLoading = false;
            }
        },

        async loadTeamRegionList(params) {
            try {
                const endpoint = '/api/teams/team-regions';
                const response = await handleRequests(endpoint);

                if (response.data && response.data.length > 0) {
                    this.teamRegionList = response.data.map((x) => {
                        return {
                            teamRegionId: x.teamRegionId,
                            name: x.name
                        };
                    });
                    this.teamRegionList.push({ teamRegionId: null, name: '(Blank)' });
                } else {
                    this.teamRegionList = [];
                }
                params.success(this.teamRegionList);
            } catch (error) {
                const message = 'Error in getting the team region list';
                showErrorMessage(this, message, error);
                this.teamRegionList = [];
                params.success(this.teamRegionList);
            } finally {
                this.teamRegionListLoading = false;
            }
        },

        refreshServerSide() {
            this.gridApi.showLoadingOverlay();
            this.gridApi.refreshServerSide({ purge: true });
        },

        filterHasLoaded() {
            this.hasGridfilterLoaded = true;
            this.setupDataList();
        },

        // eslint-disable-next-line func-names
        handleFilterOrSearch: _.debounce(function (val) {
            this.currentSearchTextValue = val.searchText;
            this.gridApi.refreshServerSide({ purge: true });
        }, 1000),
        handleUpdateOrderDetails(params, updatedData) {
            const rowNode = params.node;
            const { contact, shippingAddress, expectedDeliveryDate, readyDeliveryAvailableDate } = updatedData;

            // Use the API to update the specific cell value
            rowNode.setDataValue('shippingContact', contact.name);
            rowNode.setDataValue('shippingAddress', this.getShippingAddress(shippingAddress));
            rowNode.setDataValue('expectedDeliveryDate', expectedDeliveryDate);
            rowNode.setDataValue('readyDeliveryAvailableDate', readyDeliveryAvailableDate);
        },
        navigateToCreateOrder() {
            this.$router.push({ name: 'Create Order' });
        }
    }
};
</script>

<style scoped lang="scss">
.ag-theme-quartz {
    --ag-header-height: 40px;
    --ag-row-height: 35px;
    --ag-font-size: 13px;
}

/* Action buttons */
.action-buttons {
    display: flex;
    gap: 4px;
    justify-content: center;
}

.action-buttons .btn {
    padding: 2px 6px;
    border: none;
    border-radius: 3px;
    cursor: pointer;
}

.btn-info {
    background-color: #74b9ff;
    color: white;
}

.btn-danger {
    background-color: #e17055;
    color: white;
}

.search-container {
    display: flex;
    align-items: center;
    .search-component {
        width: 270px;
    }
    padding: 12px 0;

    ::v-deep .bulk-action-container {
        width: 500px;
    }

    .action-container {
        margin-left: 12px;
    }

    .filter-container {
        margin-left: auto;
    }
}

.grand-total-row {
    font-weight: 600;
}
</style>
